import React from 'react';
import { motion } from 'framer-motion';
import './LoadingComponents.scss';

// Skeleton Loader Component
export const SkeletonLoader = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px',
  className = '' 
}) => (
  <motion.div 
    className={`skeleton-loader ${className}`}
    style={{ width, height, borderRadius }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.15 }}
  />
);

// Card Skeleton Component
export const CardSkeleton = ({ className = '' }) => (
  <motion.div 
    className={`card-skeleton ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
  >
    <div className="card-skeleton-header">
      <SkeletonLoader width="60%" height="24px" />
      <SkeletonLoader width="20%" height="16px" />
    </div>
    <div className="card-skeleton-body">
      <SkeletonLoader width="100%" height="16px" />
      <SkeletonLoader width="80%" height="16px" />
      <SkeletonLoader width="90%" height="16px" />
    </div>
    <div className="card-skeleton-footer">
      <SkeletonLoader width="100px" height="32px" borderRadius="16px" />
      <SkeletonLoader width="80px" height="32px" borderRadius="16px" />
    </div>
  </motion.div>
);

// Table Skeleton Component
export const TableSkeleton = ({ 
  rows = 5, 
  columns = 4, 
  className = '',
  showHeader = true,
  headerHeight = '18px',
  rowHeight = '14px',
  responsive = true 
}) => (
  <motion.div 
    className={`table-skeleton ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
  >
    <table className={`table table-skeleton-table ${responsive ? 'table-responsive' : ''} ${className}`}>
      {showHeader && (
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, index) => (
              <th key={`header-${index}`}>
                <SkeletonLoader 
                  width={index === 0 ? "50%" : index === columns - 1 ? "30%" : "70%"} 
                  height={headerHeight} 
                />
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <motion.tr 
            key={`row-${rowIndex}`}
            className="table-skeleton-row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: rowIndex * 0.03, duration: 0.15 }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => {
              // Vary the width for more realistic appearance
              const getWidth = () => {
                if (colIndex === 0) return "50%"; // First column (usually ID/index)
                if (colIndex === columns - 1) return "35%"; // Last column (usually actions)
                return Math.random() > 0.5 ? "80%" : "65%"; // Middle columns
              };

              return (
                <td key={`cell-${rowIndex}-${colIndex}`}>
                  <SkeletonLoader 
                    width={getWidth()} 
                    height={rowHeight} 
                  />
                </td>
              );
            })}
          </motion.tr>
        ))}
      </tbody>
    </table>
  </motion.div>
);

// Spinner Component with different variants
export const Spinner = ({ 
  size = 'md', 
  variant = 'primary', 
  className = '',
  text = null 
}) => {
  const sizeClasses = {
    sm: 'spinner-sm',
    md: 'spinner-md',
    lg: 'spinner-lg',
    xl: 'spinner-xl'
  };

  const spinnerVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: { duration: 0.15 }
    }
  };

  const dotVariants = {
    initial: { scale: 0 },
    animate: { 
      scale: [0, 1, 0],
      transition: {
        duration: 1.2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.div 
      className={`spinner-container ${className}`}
      variants={spinnerVariants}
      initial="initial"
      animate="animate"
    >
      <div className={`spinner ${sizeClasses[size]} spinner-${variant}`}>
        <motion.div className="spinner-dot" variants={dotVariants} style={{ animationDelay: '0s' }} />
        <motion.div className="spinner-dot" variants={dotVariants} style={{ animationDelay: '0.2s' }} />
        <motion.div className="spinner-dot" variants={dotVariants} style={{ animationDelay: '0.4s' }} />
      </div>
      {text && (
        <motion.p 
          className="spinner-text"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.15 }}
        >
          {text}
        </motion.p>
      )}
    </motion.div>
  );
};

// Pulse Loader Component
export const PulseLoader = ({ className = '' }) => (
  <motion.div 
    className={`pulse-loader ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.15 }}
  >
    {[0, 1, 2].map((index) => (
      <motion.div
        key={index}
        className="pulse-dot"
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.7, 1, 0.7]
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          delay: index * 0.2
        }}
      />
    ))}
  </motion.div>
);

// Progress Bar Component
export const ProgressBar = ({ 
  progress = 0, 
  variant = 'primary', 
  height = '8px',
  animated = true,
  showLabel = false,
  className = '' 
}) => {
  const progressVariants = {
    initial: { width: 0 },
    animate: { 
      width: `${progress}%`,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <motion.div 
      className={`progress-container ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
    >
      <div className="progress-bar-wrapper" style={{ height }}>
        <motion.div 
          className={`progress-bar progress-bar-${variant} ${animated ? 'animated' : ''}`}
          variants={progressVariants}
          initial="initial"
          animate="animate"
        />
      </div>
      {showLabel && (
        <motion.span 
          className="progress-label"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {Math.round(progress)}%
        </motion.span>
      )}
    </motion.div>
  );
};

// Wave Loader Component
export const WaveLoader = ({ className = '' }) => (
  <motion.div 
    className={`wave-loader ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.15 }}
  >
    {[0, 1, 2, 3, 4].map((index) => (
      <motion.div
        key={index}
        className="wave-bar"
        animate={{
          scaleY: [1, 2, 1]
        }}
        transition={{
          duration: 1,
          repeat: Infinity,
          delay: index * 0.1
        }}
      />
    ))}
  </motion.div>
);

// Page Loading Component
export const PageLoader = ({ text = 'Loading...', className = '' }) => (
  <motion.div 
    className={`page-loader ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
  >
    <div className="page-loader-content">
      <motion.div
        className="page-loader-logo"
        animate={{
          rotate: 360
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        <div className="logo-circle">
          <span>P</span>
        </div>
      </motion.div>
      <motion.h3
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        {text}
      </motion.h3>
      <motion.div
        className="loading-dots"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            className="loading-dot"
            animate={{
              y: [0, -10, 0]
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: index * 0.1
            }}
          />
        ))}
      </motion.div>
    </div>
  </motion.div>
);

// Button Loading State
export const ButtonLoader = ({ size = 'sm', className = '' }) => (
  <motion.div 
    className={`button-loader ${className}`}
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0 }}
    transition={{ duration: 0.2 }}
  >
    <motion.div
      className={`button-spinner button-spinner-${size}`}
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
    />
  </motion.div>
);

// Chart Skeleton Components for Dashboard Analytics

// Bar Chart Skeleton
export const BarChartSkeleton = ({ 
  bars = 8, 
  className = '',
  height = '300px',
  showAxes = true,
  showLegend = true 
}) => (
  <motion.div 
    className={`chart-skeleton bar-chart-skeleton ${className}`}
    style={{ height }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="chart-container">
      {showLegend && (
        <div className="chart-legend">
          <div className="legend-items">
            <SkeletonLoader width="80px" height="16px" />
            <SkeletonLoader width="70px" height="16px" />
          </div>
        </div>
      )}
      
      <div className="chart-content">
        {showAxes && (
          <div className="chart-axes">
            <div className="y-axis">
              {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonLoader key={`y-${index}`} width="40px" height="12px" />
              ))}
            </div>
          </div>
        )}
        
        <div className="chart-bars">
          {Array.from({ length: bars }).map((_, index) => {
            const randomHeight = 40 + Math.random() * 50; // 40-90% height
            return (
              <motion.div
                key={`bar-${index}`}
                className="skeleton-bar"
                style={{ height: `${randomHeight}%` }}
                initial={{ height: 0 }}
                animate={{ height: `${randomHeight}%` }}
                transition={{ 
                  delay: index * 0.1, 
                  duration: 0.6,
                  ease: "easeOut"
                }}
              >
                <SkeletonLoader width="100%" height="100%" borderRadius="4px 4px 0 0" />
              </motion.div>
            );
          })}
        </div>
        
        {showAxes && (
          <div className="x-axis">
            {Array.from({ length: bars }).map((_, index) => (
              <SkeletonLoader key={`x-${index}`} width="60px" height="12px" />
            ))}
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

// Donut Chart Skeleton
export const DonutChartSkeleton = ({ 
  className = '',
  size = '200px',
  showLegend = true 
}) => (
  <motion.div 
    className={`chart-skeleton donut-chart-skeleton ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="donut-container">
      <div className="donut-chart" style={{ width: size, height: size }}>
        <motion.div
          className="donut-ring"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ scale: 1, rotate: 360 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <SkeletonLoader width="100%" height="100%" borderRadius="50%" />
        </motion.div>
        <div className="donut-center">
          <SkeletonLoader width="80px" height="16px" />
          <SkeletonLoader width="60px" height="14px" />
        </div>
      </div>
      
      {showLegend && (
        <div className="donut-legend">
          <div className="legend-item">
            <SkeletonLoader width="16px" height="16px" borderRadius="50%" />
            <SkeletonLoader width="60px" height="14px" />
            <SkeletonLoader width="30px" height="14px" />
          </div>
          <div className="legend-item">
            <SkeletonLoader width="16px" height="16px" borderRadius="50%" />
            <SkeletonLoader width="50px" height="14px" />
            <SkeletonLoader width="30px" height="14px" />
          </div>
        </div>
      )}
    </div>
  </motion.div>
);

// Financial Chart Skeleton (Multi-bar chart)
export const FinancialChartSkeleton = ({ 
  months = 12, 
  className = '',
  height = '400px' 
}) => (
  <motion.div 
    className={`chart-skeleton financial-chart-skeleton ${className}`}
    style={{ height }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="chart-container">
      <div className="chart-header">
        <SkeletonLoader width="200px" height="20px" />
        <div className="chart-legend">
          <div className="legend-items">
            {['Received', 'Expenses', 'Pending'].map((_, index) => (
              <div key={index} className="legend-item">
                <SkeletonLoader width="16px" height="16px" borderRadius="3px" />
                <SkeletonLoader width="60px" height="14px" />
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="chart-content">
        <div className="y-axis">
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonLoader key={`y-${index}`} width="50px" height="12px" />
          ))}
        </div>
        
        <div className="chart-bars-group">
          {Array.from({ length: months }).map((_, monthIndex) => (
            <div key={`month-${monthIndex}`} className="month-group">
              {Array.from({ length: 3 }).map((_, barIndex) => {
                const randomHeight = 30 + Math.random() * 60; // 30-90% height
                return (
                  <motion.div
                    key={`bar-${monthIndex}-${barIndex}`}
                    className="skeleton-financial-bar"
                    style={{ height: `${randomHeight}%` }}
                    initial={{ height: 0 }}
                    animate={{ height: `${randomHeight}%` }}
                    transition={{ 
                      delay: (monthIndex * 3 + barIndex) * 0.05, 
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                  >
                    <SkeletonLoader width="100%" height="100%" borderRadius="2px" />
                  </motion.div>
                );
              })}
            </div>
          ))}
        </div>
        
        <div className="x-axis">
          {Array.from({ length: months }).map((_, index) => (
            <SkeletonLoader key={`x-${index}`} width="40px" height="12px" />
          ))}
        </div>
      </div>
    </div>
  </motion.div>
);

// Dashboard Stats Card Skeleton
export const StatCardSkeleton = ({ className = '' }) => (
  <motion.div 
    className={`stat-card-skeleton ${className}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className="stat-card-content">
      <div className="stat-icon-skeleton">
        <SkeletonLoader width="48px" height="48px" borderRadius="12px" />
      </div>
      <div className="stat-details-skeleton">
        <div className="stat-header-skeleton">
          <SkeletonLoader width="80px" height="14px" />
          <SkeletonLoader width="40px" height="12px" />
        </div>
        <div className="stat-value-skeleton">
          <SkeletonLoader width="60px" height="24px" />
          <SkeletonLoader width="100px" height="12px" />
        </div>
      </div>
    </div>
  </motion.div>
);

const LoadingComponents = {
  SkeletonLoader,
  CardSkeleton,
  TableSkeleton,
  Spinner,
  PulseLoader,
  ProgressBar,
  WaveLoader,
  PageLoader,
  ButtonLoader,
  BarChartSkeleton,
  DonutChartSkeleton,
  FinancialChartSkeleton,
  StatCardSkeleton
};

export default LoadingComponents;